// Libs
import { Box, Grid2, InputLabel, Stack, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseFilters, BaseSelect, CommonDatePicker, Loading } from '~/components/common';
import { ColumnChart } from '~/components/module/report';
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
// Others
import { varColor } from '~/themes/themeVariables';
import { getEmployeeList } from '~/thunks/employee/employeeThunk';
import { getReportPurchaseOderTotal } from '~/thunks/report/reportThunk';
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ONE,
  EMPTY_STRING,
  technicianPoTotalLegend,
  techReportOptions,
} from '~/utils/constants/common';
import { DateFormatEnum, ReportFilterShowByEnum } from '~/utils/enum/common';
import { formatCurrency, getFullName } from '~/utils/helper/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { IBaseOption, IQueryParamBase } from '~/utils/interface/common';
import { IFormatDataReportRes, IQueryParamReport, IReportTechnicianCommon } from '~/utils/interface/report';
import { useAppDispatch } from '~/utils/redux/hooks';
// Styles, images, icons
import styles from './ReportPoPerPayPeriod.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const defaultFilterVal: IQueryParamReport = {
  showBy: ReportFilterShowByEnum.MONTH,
  startTime: moment().startOf('month').format(DateFormatEnum.YYYY_MM_DD),
  endTime: moment().endOf('month').format(DateFormatEnum.YYYY_MM_DD),
};

const ReportPoPerPayPeriod = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/report', 'common/base']);
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [paramObject, setParamObject] = useState<IQueryParamReport>(defaultFilterVal);
  const [filterValues, setFilterValues] = useState<IQueryParamReport>(defaultFilterVal);

  const [employeeList, setEmployeeList] = useState<IBaseOption[]>([]);
  const [employeeSearchKey, setEmployeeSearchKey] = useState<string>(EMPTY_STRING);
  const debounceEmployeeSearchKey = useDebounce(employeeSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [reportPoTotal, setReportPoTotal] = useState<IReportTechnicianCommon>({ chart: [] });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingEmployee, setIsLoadingEmployee] = useState<boolean>(false);
  //#endregion Declare State

  const preparedChartPoTotal = useCallback(
    (data: IFormatDataReportRes): IReportTechnicianCommon => {
      if (!data?.chart) return { chart: [], categories: [] };

      const chartFormatted = data.chart.map((item) => ({
        name: t(technicianPoTotalLegend[item.chartType]),
        data: item.chartData,
      }));

      return { ...data, chart: chartFormatted };
    },
    [t]
  );

  const handleGetPoTotal = useCallback(
    (params: IQueryParamReport) => {
      setIsLoading(true);

      dispatch(getReportPurchaseOderTotal(params))
        .unwrap()
        .then((resp) => {
          if (!resp) return;

          const data = preparedChartPoTotal(resp);
          setReportPoTotal(data);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, preparedChartPoTotal]
  );

  const handleFetchOptionsEmployee = useCallback(() => {
    setIsLoadingEmployee(true);

    const params: IQueryParamBase = {
      page: DEFAULT_NUMBER_ONE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceEmployeeSearchKey && { textSearch: debounceEmployeeSearchKey }),
    };
    dispatch(getEmployeeList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;
        if (!responses) return;

        const dataEmployee: IBaseOption[] = responses.map((item) => ({
          value: item.id,
          label: getFullName({ firstName: item.firstName, lastName: item.lastName }),
        }));

        setEmployeeList(dataEmployee);
        setParamObject((prev) =>
          prev?.employeeIds ? prev : { ...prev, employeeIds: dataEmployee[0]?.value }
        );
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingEmployee(false);
      });
  }, [dispatch, debounceEmployeeSearchKey]);

  //#region Implement Hook
  useEffect(() => {
    if (!paramObject?.employeeIds || !paramObject?.showBy || !paramObject?.startTime || !paramObject?.endTime)
      return;

    handleGetPoTotal(paramObject);
  }, [paramObject, handleGetPoTotal]);

  useEffect(() => {
    handleFetchOptionsEmployee();
  }, [debounceEmployeeSearchKey, handleFetchOptionsEmployee]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSearchDropdownEmployee = (keySearch: string) => {
    setEmployeeSearchKey(keySearch);
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleApplyFilter = (values: IQueryParamReport | undefined) => {
    if (!values) {
      setFilterValues(defaultFilterVal);
      return;
    }

    setFilterValues({ ...filterValues, ...values });

    if (values?.showBy || values?.startTime || values?.endTime) {
      setParamObject({ ...paramObject, ...values });
    }
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='reportPoPerPayPeriod'
      title={t('report_po_per_pay_period_page_title')}
      onBackToPreviousPage={handleBack}
      childrenToolbar={
        <>
          <BaseSelect
            layout='horizontal'
            width={240}
            options={employeeList || []}
            value={paramObject?.employeeIds || EMPTY_STRING}
            isLoading={isLoadingEmployee}
            onSearchChange={handleSearchDropdownEmployee}
            onChange={(option: IBaseOption) =>
              setParamObject((prev) => ({ ...prev, employeeIds: option?.value }))
            }
          />
          <BaseFilters value={filterValues} defaultValue={defaultFilterVal} onApply={handleApplyFilter}>
            {({ value, onChange }) => (
              <Stack gap={'12px'}>
                <Typography className={cx('title')}>
                  {t('common_action_filter_by', { ns: 'common/base' })}
                </Typography>

                <Box component={'div'} className={cx('filterGroup')}>
                  <InputLabel className={cx('label')}>{t('report_technician_filter_period')}</InputLabel>
                  <BaseSelect
                    layout='horizontal'
                    width={240}
                    options={techReportOptions}
                    value={value?.showBy}
                    onChange={(option: IBaseOption) => {
                      onChange && onChange({ name: 'showBy', value: option?.value });

                      let startTime = value?.startTime;
                      let endTime = value?.endTime;

                      if (option?.value === ReportFilterShowByEnum.MONTH) {
                        startTime = moment().startOf('month').format(DateFormatEnum.YYYY_MM_DD);
                        endTime = moment().endOf('month').format(DateFormatEnum.YYYY_MM_DD);
                      } else if (option?.value === ReportFilterShowByEnum.YEAR) {
                        startTime = moment().startOf('year').format(DateFormatEnum.YYYY_MM_DD);
                        endTime = moment().endOf('year').format(DateFormatEnum.YYYY_MM_DD);
                      }

                      onChange && onChange({ name: 'startTime', value: startTime });
                      onChange && onChange({ name: 'endTime', value: endTime });
                    }}
                  />
                </Box>

                {value?.showBy && value.showBy === ReportFilterShowByEnum.DAY && (
                  <Box component={'div'} className={cx('filterGroup')}>
                    <InputLabel className={cx('label')}>
                      {t('report_technician_filter_custom_date')}
                    </InputLabel>
                    <CommonDatePicker
                      mode='rangeDate'
                      value={{
                        startDate: value?.startTime || EMPTY_STRING,
                        endDate: value?.endTime || EMPTY_STRING,
                      }}
                      width={240}
                      onChange={({ startDate, endDate }) => {
                        onChange && onChange({ name: 'startTime', value: startDate });
                        onChange && onChange({ name: 'endTime', value: endDate });
                      }}
                    />
                  </Box>
                )}
              </Stack>
            )}
          </BaseFilters>
        </>
      }
    >
      <>
        <Grid2 container size={12} rowSpacing={'20px'} columnSpacing={'20px'}>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
            <ColumnChart
              title={t('chart_technician_po_total_title')}
              data={reportPoTotal}
              categories={reportPoTotal?.categories ?? []}
              colors={[varColor.backgroundMenuItemIconActive]}
              yAxisFormatter={(val) => formatCurrency(Number(val))}
            />
          </Grid2>
        </Grid2>

        {isLoading && <Loading />}
      </>
    </ChildrenPage>
  );
};

export default ReportPoPerPayPeriod;
